import { supabase } from '../lib/supabase';

/**
 * Chaine vide -> null, pour les colonnes que Postgres refuse de lire en ''.
 *
 * Motif repris de subcontractorService.js:154-157. PAS `|| null` : celui-ci
 * transformerait aussi 0 et false en null. Ici, seule la chaine vide compte.
 */
const emptyToNull = (v) => (v === '' ? null : v);

/**
 * Convert snake_case database fields to camelCase for React
 */
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const out = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
    out[camelKey] = obj?.[key];
  }
  return out;
}

/**
 * Convert camelCase React fields to snake_case for database
 */
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    out[snakeKey] = obj?.[key];
  }
  return out;
}

export const expensesService = {

  /**
   * Get all expenses for a project, sorted by expense_date DESC
   * @param {string} projectId
   * @returns {Promise<Array>}
   */
  async getByProject(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('project_expenses')
        ?.select('*')
        ?.eq('project_id', projectId)
        ?.order('expense_date', { ascending: false })
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('[expensesService.getByProject]', error?.message);
        return [];
      }
      return (data || [])?.map(toCamelCase);
    } catch (err) {
      console.error('[expensesService.getByProject]', err);
      return [];
    }
  },

  /**
   * Create a new expense
   * @param {Object} data - expense data (camelCase)
   * @returns {Promise<Object>} Created expense
   */
  async create(data) {
    try {
      const amount = parseFloat(data?.amount) || 0;
      const taxAmount = parseFloat(data?.taxAmount) || 0;
      const totalAmount = amount + taxAmount;

      const payload = {
        project_id: data?.projectId,
        division_code: data?.divisionCode || null,
        cost_code: data?.costCode || null,
        description: data?.description || null,
        vendor: data?.vendor || null,
        amount,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        expense_date: data?.expenseDate || null,
        reference_number: data?.referenceNumber || null,
        status: data?.status || 'paid',
        extra_id: data?.extraId || null,
        paid_by: data?.paidBy || null,
        payment_request_id: data?.paymentRequestId || null,
      };

      const { data: row, error } = await supabase
        ?.from('project_expenses')
        ?.insert(payload)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(row);
    } catch (err) {
      console.error('[expensesService.create]', err);
      throw err;
    }
  },

  /**
   * Update an existing expense
   * @param {string} id - expense UUID
   * @param {Object} data - fields to update (camelCase)
   * @returns {Promise<Object>} Updated expense
   */
  async update(id, data) {
    try {
      const payload = {};
      if (data?.divisionCode !== undefined) payload.division_code = data?.divisionCode;
      if (data?.costCode !== undefined) payload.cost_code = data?.costCode;
      if (data?.description !== undefined) payload.description = data?.description;
      if (data?.vendor !== undefined) payload.vendor = data?.vendor;
      // ExpensesPage refuse une date vide en amont, mais le service etait nu.
      if (data?.expenseDate !== undefined) payload.expense_date = emptyToNull(data?.expenseDate);
      if (data?.referenceNumber !== undefined) payload.reference_number = data?.referenceNumber;
      if (data?.status !== undefined) payload.status = data?.status;
      if (data?.extraId !== undefined) payload.extra_id = data?.extraId || null;
      if (data?.paidBy !== undefined) payload.paid_by = data?.paidBy || null;
      if (data?.paymentRequestId !== undefined) payload.payment_request_id = data?.paymentRequestId || null;

      if (data?.amount !== undefined || data?.taxAmount !== undefined) {
        const amount = parseFloat(data?.amount) ?? 0;
        const taxAmount = parseFloat(data?.taxAmount) ?? 0;
        payload.amount = amount;
        payload.tax_amount = taxAmount;
        payload.total_amount = amount + taxAmount;
      }

      const { data: row, error } = await supabase
        ?.from('project_expenses')
        ?.update(payload)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(row);
    } catch (err) {
      console.error('[expensesService.update]', err);
      throw err;
    }
  },

  /**
   * Delete an expense
   * @param {string} id - expense UUID
   * @returns {Promise<boolean>}
   */
  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('project_expenses')
        ?.delete()
        ?.eq('id', id);
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('[expensesService.delete]', err);
      throw err;
    }
  }
};

export default expensesService;
